import React, { useState, useRef, useEffect } from 'react';
import { bool, node, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import * as validators from '../../../util/validators';
import { getPropsForCustomUserFieldInputs, USER_TYPES } from '../../../util/userHelpers';

import { Form, PrimaryButton, FieldTextInput, NamedLink, CustomExtendedDataField, FieldCheckboxGroup, Heading, FieldLocationAutocompleteInput, FieldSelect } from '../../../components';

import FieldSelectUserType from '../FieldSelectUserType';

import css from './SignupForm.module.css';
import { prepareToState } from '../../../util/urlHelpers';
import { findValues } from '../../../util/fieldHelpers';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const getSoleUserTypeMaybe = userTypes =>
  Array.isArray(userTypes) && userTypes.length === 1 ? userTypes[0].userType : null;

const SignupFormComponent = props => {
  const [turnstileLoaded, setTurnstileLoaded] = useState(false);
  const [turnstileRendered, setTurnstileRendered] = useState(false);
  const turnstileRef = useRef(null);
  const turnstileContainerRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!TURNSTILE_SITE_KEY) {
      // No Turnstile configured — allow form submission without it
      setTurnstileRendered(true);
      return;
    }
    if (window.turnstile) {
      setTurnstileLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    script.onload = () => setTurnstileLoaded(true);
    script.onerror = () => {
      // Adblocker or CSP blocked the script — allow fallback
      console.warn('Turnstile script failed to load (blocked by adblocker or CSP)');
      setTurnstileRendered(true);
    };
    document.head.appendChild(script);

    // Timeout fallback: if script doesn't load in 10s, allow form anyway
    const timeout = setTimeout(() => {
      if (!turnstileRef.current) {
        console.warn('Turnstile script load timed out');
        setTurnstileRendered(true);
      }
    }, 10000);

    return () => clearTimeout(timeout);
  }, []);

  // Render the Turnstile widget explicitly when the container is ready
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    if (!turnstileLoaded || !turnstileContainerRef.current) return;
    // Prevent double-render
    if (turnstileRef.current) return;

    try {
      const widgetId = window.turnstile.render(turnstileContainerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: 'light',
        size: 'normal',
      });
      turnstileRef.current = widgetId;
      setTurnstileRendered(true);
    } catch (err) {
      console.error('Turnstile render error:', err);
      setTurnstileRendered(true); // Allow form fallback on render error
    }
  }, [turnstileLoaded]);

  return (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    initialValues={{ userType: props.preselectedUserType || getSoleUserTypeMaybe(props.userTypes) }}
    onSubmit={values => {
      // Inject Turnstile token into form values at FinalForm level.
      // See LoginForm for the full reasoning.
      const turnstileToken =
        TURNSTILE_SITE_KEY && turnstileRef.current
          ? window.turnstile?.getResponse(turnstileRef.current) || undefined
          : undefined;
      return props.onSubmit({ ...values, turnstileToken });
    }}
    render={formRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        intl,
        termsAndConditions,
        preselectedUserType,
        userTypes,
        userFields,
        values,
        fromState,
        isIframe,
      } = formRenderProps;

      const { userType } = values || {};

      // email
      const emailRequired = validators.required(
        intl.formatMessage({
          id: 'SignupForm.emailRequired',
        })
      );
      const emailValid = validators.emailFormatValid(
        intl.formatMessage({
          id: 'SignupForm.emailInvalid',
        })
      );

      // password
      const passwordRequiredMessage = intl.formatMessage({
        id: 'SignupForm.passwordRequired',
      });
      const passwordMinLengthMessage = intl.formatMessage(
        {
          id: 'SignupForm.passwordTooShort',
        },
        {
          minLength: validators.PASSWORD_MIN_LENGTH,
        }
      );
      const passwordMaxLengthMessage = intl.formatMessage(
        {
          id: 'SignupForm.passwordTooLong',
        },
        {
          maxLength: validators.PASSWORD_MAX_LENGTH,
        }
      );
      const passwordMinLength = validators.minLength(
        passwordMinLengthMessage,
        validators.PASSWORD_MIN_LENGTH
      );
      const passwordMaxLength = validators.maxLength(
        passwordMaxLengthMessage,
        validators.PASSWORD_MAX_LENGTH
      );
      const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);
      const passwordValidators = validators.composeValidators(
        passwordRequired,
        passwordMinLength,
        passwordMaxLength
      );

      const addressLabel = intl.formatMessage({ id: 'TeamPage.teamAddressLabel' });
      const addressPlaceholder= intl.formatMessage({ id: 'TeamPage.teamAddressPlaceholder' });

      // Custom user fields. Since user types are not supported here,
      // only fields with no user type id limitation are selected.
      const userFieldProps = getPropsForCustomUserFieldInputs(userFields, intl, userType);

      const noUserTypes = !userType && !(userTypes?.length > 0);
      const userTypeConfig = userTypes.find(config => config.userType === userType);
      const showDefaultUserFields = userType || noUserTypes;
      const showCustomUserFields = (userType || noUserTypes) && userFieldProps?.length > 0;

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const turnstileDisabled = TURNSTILE_SITE_KEY && !turnstileRendered;
      const submitDisabled = invalid || submitInProgress || turnstileDisabled;

      let toState = prepareToState(fromState, isIframe);

      const link = <NamedLink name="LoginPage" className={css.logInLink} {...toState}>
          <FormattedMessage id="SignupForm.logInLinkText" />
        </NamedLink>;

      const isProvider = preselectedUserType == USER_TYPES.PROVIDER;
      const isBuyer = preselectedUserType == USER_TYPES.BUYER;

      const identity = v => v;

      const handleSetAddress = (fValues) => {
        let { context = [], address, text, place_name } = fValues?.prediction || {};
        let { origin } = fValues?.place || {};
        let street1 = `${address} ${text}`;

        if (!place_name.startsWith(street1)) {
          const streetFromPlaceNameItems = place_name.split(',')[0];
          if (streetFromPlaceNameItems.length) {
            street1 = streetFromPlaceNameItems[0];
          }
        }

        let zip = findValues(context, 'postcode');
        let city = findValues(context, 'place');
        let state = findValues(context, 'region');
        let country = findValues(context, 'country', 'short_code', true);

        let payload = {
          storedAddress: {
            place_name,
            origin
          },
          shippoAddress: {
            street1,
            zip,
            city,
            state,
            country
          }
        };
        props.processAddress(payload);
      };

      const addressRequiredMessage = intl.formatMessage({
        id: 'EditListingDeliveryForm.addressRequired',
      });
      const addressNotRecognizedMessage = intl.formatMessage({
        id: 'EditListingDeliveryForm.addressNotRecognized',
      });

      const hideFields = true;

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          {isBuyer ? (
            <div className={css.buyerHeadingContainer}>
              <Heading as="h1" rootClassName={css.buyerHeading}>
                It's hunting season somewhere.<br />Don't miss out.
              </Heading>
            </div>
          ) : null}

          <FieldSelectUserType
            name="userType"
            userTypes={userTypes}
            hasExistingUserType={!!preselectedUserType}
            intl={intl}
          />

          {isProvider ? <>
                <Heading as="h4" className={css.businessInformationHeader}>
                  <FormattedMessage id="TeamPage.businessInformation" />
                </Heading>
                <FieldSelect
                className={css.select}
                name="sellerType"
                id="sellerType"
                label={
                  <>
                    {intl.formatMessage({ id: 'SignupForm.sellerType' })}{' '}
                    <span className={css.requiredText}>(required)</span>
                  </>
                }
                validate={validators.required(
                  intl.formatMessage({ id: 'SignupForm.sellerTypeRequired' })
                )}
              >
                <option hidden value="">
                  {intl.formatMessage({ id: 'SignupForm.sellerTypePlaceholder' })}
                </option>
                <option value="owner">
                  {intl.formatMessage({ id: 'SignupForm.sellerTypeOwner' })}
                </option>
                <option value="manufacturer">
                  {intl.formatMessage({ id: 'SignupForm.sellerTypeManufacturer' })}
                </option>
                <option value="inventoryPartner">
                  {intl.formatMessage({ id: 'SignupForm.sellerTypeInventoryPartner' })}
                </option>
                {/*<option value="insider">
                  {intl.formatMessage({ id: 'SignupForm.sellerTypeInsider' })}
                </option>
                <option value="innovator">
                  {intl.formatMessage({ id: 'SignupForm.sellerTypeInnovator' })}
                </option>*/}
                </FieldSelect> 
                <FieldTextInput
                  className={css.text}
                  type="text"
                  id="businessName"
                  name="businessName"
                  label={
                    <>
                      {intl.formatMessage({ id: 'TeamPage.businessName' })}{' '}
                      <span className={css.requiredText}>(required)</span>
                    </>
                  }
                  placeholder={intl.formatMessage({ id: 'TeamPage.businessName' })}
                  validate={validators.required(intl.formatMessage({ id: 'TeamPage.businessNameRequired' }))}
                />
                <FieldTextInput
                  className={css.text}
                  type="tel"
                  id="businessPrimaryPhone"
                  name="businessPrimaryPhone"
                  label={
                    <>
                      {intl.formatMessage({ id: 'TeamPage.businessPrimaryPhone' })}{' '}
                      <span className={css.requiredText}>(required)</span>
                    </>
                  }
                  placeholder={intl.formatMessage({ id: 'TeamPage.businessPrimaryPhone' })}
                  validate={validators.composeValidators(
                    validators.required(intl.formatMessage({ id: 'TeamPage.businessPrimaryPhoneRequired' })),
                    validators.validPhone(intl.formatMessage({ id: 'SignupForm.validPhone' }))
                  )}
                />
                <FieldTextInput
                  className={css.text}
                  type="text"
                  id="businessWebsite"
                  name="businessWebsite"
                  label={
                    <>
                      {intl.formatMessage({ id: 'TeamPage.businessWebsite' })}{' '}
                      <span className={css.requiredText}>(required)</span>
                    </>
                  }
                  placeholder={intl.formatMessage({ id: 'TeamPage.businessWebsite' })}
                  validate={validators.required(intl.formatMessage({ id: 'TeamPage.businessWebsiteRequired' }))}
                />

                {!hideFields ? <><FieldLocationAutocompleteInput
                  onSetAddress={handleSetAddress}
                  rootClassName={css.text}
                  inputClassName={css.locationAutocompleteInput}
                  iconClassName={css.locationAutocompleteInputIcon}
                  predictionsClassName={css.predictionsRoot}
                  validClassName={css.validLocation}
                  autoFocus={false}
                  name="businessLocation"
                  // label={addressLabel}
                  placeholder={addressPlaceholder}
                  useDefaultPredictions={false}
                  format={identity}
                  // valueFromForm={values.businessLocation}
                  validate={
                    validators.composeValidators(
                        validators.autocompleteSearchRequired(addressRequiredMessage),
                        validators.autocompletePlaceSelected(addressNotRecognizedMessage)
                      )
                  }
                  hideErrorMessage={false}
                  // Whatever parameters are being used to calculate
                  // the validation function need to be combined in such
                  // a way that, when they change, this key prop
                  // changes, thus reregistering this field (and its
                  // validation function) with Final Form.
                  // See example: https://codesandbox.io/s/changing-field-level-validators-zc8ei
                  key="locationValidation"
                />

                <Heading as="h4">
                  <FormattedMessage id="TeamPage.inventoryInformation" />
                </Heading>

                <FieldTextInput
                  className={css.text}
                  type="number"
                  id="expectedItems"
                  name="expectedItems"
                  label={
                    <>
                      {intl.formatMessage({ id: 'TeamPage.expectedItems' })}{' '}
                      <span className={css.requiredText}>(required)</span>
                    </>
                  }
                  placeholder={intl.formatMessage({ id: 'TeamPage.expectedItems' })}
                  validate={validators.required(intl.formatMessage({ id: 'TeamPage.expectedItemsRequired' }))}
                />

                <p className={css.text}>{intl.formatMessage({ id: 'TeamPage.equipmentSports' })}</p>

                <FieldCheckboxGroup
                  className={css.text}
                  name="sports"
                  id="sports"
                  optionLabelClassName={css.finePrint}
                  options={[
                    { key: 'bow-hunting', label: 'Bow Hunting' },
                    { key: 'gun-hunting', label: 'Gun Hunting' },
                    { key: 'fishing', label: 'Fishing' },
                    { key: 'backcountry-camping', label: 'Backcountry Camping' },
                    { key: 'other', label: 'Other' },
                  ]}
                />

                <FieldTextInput
                  className={css.text}
                  type="text"
                  id="sportsAdditionalData"
                  name="sportsAdditionalData"
                  label={intl.formatMessage({ id: 'TeamPage.sportsAdditionalData' })}
                  placeholder={intl.formatMessage({ id: 'TeamPage.sportsAdditionalData' })}
                />

                <p className={css.text}>{intl.formatMessage({ id: 'TeamPage.equipmentConditions' })}</p>

                <FieldCheckboxGroup
                  className={css.text}
                  name="conditions"
                  id="conditions"
                  optionLabelClassName={css.finePrint}
                  options={[
                    { key: 'new', label: 'New' },
                    { key: 'sale', label: 'Sale / discount / closeout' },
                    { key: 'store-returns', label: 'Store returns' },
                    { key: 'consignment', label: 'Consignment' },
                    { key: 'rental', label: 'Rental' },
                    { key: 'other', label: 'Other' },
                  ]}
                />

                <Heading as="h4">
                  <FormattedMessage id="TeamPage.firstReference" />
                </Heading>

                <div className={css.name}>
                  <FieldTextInput
                    className={css.firstNameRoot}
                    type="text"
                    id={formId ? `${formId}.frfname` : 'frfname'}
                    name="frfname"
                    autoComplete="frfname"
                    label={
                      <>
                        {intl.formatMessage({ id: 'SignupForm.firstNameLabel' })}{' '}
                        <span className={css.requiredText}>(required)</span>
                      </>
                    }
                    placeholder={intl.formatMessage({ id: 'SignupForm.firstNamePlaceholder' })}
                    validate={validators.required(intl.formatMessage({ id: 'SignupForm.firstNameRequired' }))}
                  />

                  <FieldTextInput
                    className={css.lastNameRoot}
                    type="text"
                    id={formId ? `${formId}.frlname` : 'frlname'}
                    name="frlname"
                    autoComplete="frlname"
                    label={
                      <>
                        {intl.formatMessage({ id: 'SignupForm.lastNameLabel' })}{' '}
                        <span className={css.requiredText}>(required)</span>
                      </>
                    }
                    placeholder={intl.formatMessage({ id: 'SignupForm.lastNamePlaceholder' })}
                    validate={validators.required(intl.formatMessage({ id: 'SignupForm.lastNameRequired' }))}
                  />
                </div>

                <FieldTextInput
                  className={css.text}
                  type="text"
                  id="frBusinessRelationship"
                  name="frBusinessRelationship"
                  label={
                    <>
                      {intl.formatMessage({ id: 'TeamPage.businessRelationship' })}{' '}
                      <span className={css.requiredText}>(required)</span>
                    </>
                  }
                  placeholder={intl.formatMessage({ id: 'TeamPage.businessRelationship' })}
                  validate={validators.required(intl.formatMessage({ id: 'TeamPage.businessRelationshipRequired' }))}
                />

                <FieldTextInput
                  className={css.text}
                  type="tel"
                  id="frPhone"
                  name="frPhone"
                  label={
                    <>
                      {intl.formatMessage({ id: 'TeamPage.referencePhone' })}{' '}
                      <span className={css.requiredText}>(required)</span>
                    </>
                  }
                  placeholder={intl.formatMessage({ id: 'TeamPage.referencePhone' })}
                  validate={validators.composeValidators(
                    validators.required(intl.formatMessage({ id: 'TeamPage.referencePhoneRequired' })),
                    validators.validPhone(intl.formatMessage({ id: 'SignupForm.validPhone' }))
                  )}
                />

                <FieldTextInput
                  className={css.text}
                  type="email"
                  id="frEmail"
                  name="frEmail"
                  label={
                    <>
                      {intl.formatMessage({ id: 'TeamPage.referenceEmail' })}{' '}
                      <span className={css.requiredText}>(required)</span>
                    </>
                  }
                  placeholder={intl.formatMessage({ id: 'TeamPage.referenceEmail' })}
                  validate={validators.composeValidators(emailRequired, emailValid)}
                />

                <Heading as="h4">
                  <FormattedMessage id="TeamPage.secondReference" />
                </Heading>

                <div className={css.name}>
                  <FieldTextInput
                    className={css.firstNameRoot}
                    type="text"
                    id={formId ? `${formId}.srfname` : 'srfname'}
                    name="srfname"
                    autoComplete="srfname"
                    label={
                      <>
                        {intl.formatMessage({ id: 'SignupForm.firstNameLabel' })}{' '}
                        <span className={css.requiredText}>(required)</span>
                      </>
                    }
                    placeholder={intl.formatMessage({ id: 'SignupForm.firstNamePlaceholder' })}
                    validate={validators.required(intl.formatMessage({ id: 'SignupForm.firstNameRequired' }))}
                  />

                  <FieldTextInput
                    className={css.lastNameRoot}
                    type="text"
                    id={formId ? `${formId}.srlname` : 'srlname'}
                    name="srlname"
                    autoComplete="srlname"
                    label={
                      <>
                        {intl.formatMessage({ id: 'SignupForm.lastNameLabel' })}{' '}
                        <span className={css.requiredText}>(required)</span>
                      </>
                    }
                    placeholder={intl.formatMessage({ id: 'SignupForm.lastNamePlaceholder' })}
                    validate={validators.required(intl.formatMessage({ id: 'SignupForm.lastNameRequired' }))}
                  />
                </div>

                <FieldTextInput
                  className={css.text}
                  type="text"
                  id="srBusinessRelationship"
                  name="srBusinessRelationship"
                  label={
                    <>
                      {intl.formatMessage({ id: 'TeamPage.businessRelationship' })}{' '}
                      <span className={css.requiredText}>(required)</span>
                    </>
                  }
                  placeholder={intl.formatMessage({ id: 'TeamPage.businessRelationship' })}
                  validate={validators.required(intl.formatMessage({ id: 'TeamPage.businessRelationshipRequired' }))}
                />

                <FieldTextInput
                  className={css.text}
                  type="tel"
                  id="srPhone"
                  name="srPhone"
                  label={
                    <>
                      {intl.formatMessage({ id: 'TeamPage.referencePhone' })}{' '}
                      <span className={css.requiredText}>(required)</span>
                    </>
                  }
                  placeholder={intl.formatMessage({ id: 'TeamPage.referencePhone' })}
                  validate={validators.composeValidators(
                    validators.required(intl.formatMessage({ id: 'TeamPage.referencePhoneRequired' })),
                    validators.validPhone(intl.formatMessage({ id: 'SignupForm.validPhone' }))
                  )}
                />

                <FieldTextInput
                  className={css.text}
                  type="email"
                  id="srEmail"
                  name="srEmail"
                  label={
                    <>
                      {intl.formatMessage({ id: 'TeamPage.referenceEmail' })}{' '}
                      <span className={css.requiredText}>(required)</span>
                    </>
                  }
                  placeholder={intl.formatMessage({ id: 'TeamPage.referenceEmail' })}
                  validate={validators.composeValidators(emailRequired, emailValid)}
                />
              </> : null}

              {/* <UserFieldPhoneNumber
                formName="SignupForm"
                className={css.row}
                userTypeConfig={userTypeConfig}
                intl={intl}
              /> */}
              </> : null}

          {showDefaultUserFields ? (
            <div className={css.defaultUserFields}>
              
              {isProvider ? <Heading as="h4" className={css.personalInformationHeader}>
                <FormattedMessage id="TeamPage.personalInformation" />
              </Heading> : null}
              <div className={css.name}>
                <FieldTextInput
                  className={classNames(css.text, css.firstNameRoot, isBuyer && css.buyerInput)}
                  type="text"
                  id={formId ? `${formId}.fname` : 'fname'}
                  name="fname"
                  autoComplete="given-name"
                  label={
                    <>
                      {intl.formatMessage({ id: 'SignupForm.firstNameLabel' })}{' '}
                      <span className={css.requiredText}>(required)</span>
                    </>
                  }
                  validate={validators.required(
                    intl.formatMessage({
                      id: 'SignupForm.firstNameRequired',
                    })
                  )}
                />
                <FieldTextInput
                  className={classNames(css.lastNameRoot, css.text, isBuyer && css.buyerInput) }
                  type="text"
                  id={formId ? `${formId}.lname` : 'lname'}
                  name="lname"
                  autoComplete="family-name"
                  label={
                    <>
                      {intl.formatMessage({ id: 'SignupForm.lastNameLabel' })}{' '}
                      <span className={css.requiredText}>(required)</span>
                    </>
                  }
                  validate={validators.required(
                    intl.formatMessage({
                      id: 'SignupForm.lastNameRequired',
                    })
                  )}
                />
              </div>

              {/* <UserFieldDisplayName
                formName="SignupForm"
                className={css.row}
                userTypeConfig={userTypeConfig}
                intl={intl}
              /> */}

              {!isBuyer ? <FieldTextInput
                type="tel"
                className={css.text}
                id={formId ? `${formId}.phone` : 'phone'}
                name="phone"
                autoComplete="phone"
                label={
                  <>
                    {intl.formatMessage({ id: 'SignupForm.phone' })}{' '}
                    <span className={css.requiredText}>(required)</span>
                  </>
                }
                validate={validators.composeValidators(
                  validators.required(intl.formatMessage({ id: 'SignupForm.phoneRequired' })),
                  validators.validPhone(intl.formatMessage({ id: 'SignupForm.validPhone' })),
                )}
              /> : null}

              <FieldTextInput
                type="email"
                className={classNames(css.text, css.email, isBuyer && css.buyerInput)}
                id={formId ? `${formId}.email` : 'email'}
                name="email"
                autoComplete="email"
                label={
                  <>
                    {intl.formatMessage({ id: 'SignupForm.emailLabel' })}{' '}
                    <span className={css.requiredText}>(required)</span>
                  </>
                }
                validate={validators.composeValidators(emailRequired, emailValid)}
              />

              <FieldTextInput
                className={classNames(css.text, css.password, isBuyer && css.buyerInput)}
                type="password"
                id={formId ? `${formId}.password` : 'password'}
                name="password"
                autoComplete="new-password"
                label={
                  <div>
                    <div>
                      {intl.formatMessage({ id: 'SignupForm.passwordLabel' })}{' '}
                      <span className={css.requiredText}>(required)</span>
                    </div>
                    <div className={css.passwordHelper}>
                      Must be at least 8 characters
                    </div>
                  </div>
                }
                validate={passwordValidators}
              />

              <FieldTextInput
                className={classNames(css.text, css.password, isBuyer && css.buyerInput)}
                type="password"
                id={formId ? `${formId}.confirmPassword` : 'confirmPassword'}
                name="confirmPassword"
                autoComplete="new-password"
                label={
                  <>
                    {intl.formatMessage({ id: 'SignupForm.confirmPasswordLabel' })}{' '}
                    <span className={css.requiredText}>(required)</span>
                  </>
                }
                validate={validators.composeValidators(
                  passwordRequired,
                  validators.passwordsMatch(
                    intl.formatMessage({ id: 'SignupForm.passwordsMustMatch' })
                  )
                )}
              />

              {/* Honeypot field - hidden from users, catches bots */}
              <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
                <FieldTextInput
                  type="text"
                  id="website"
                  name="website"
                  placeholder="Leave this field blank"
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>
            </div>
          ) : null}

          {showCustomUserFields ? (
            <div className={css.customFields}>
              {userFieldProps.map(fieldProps => (
                <CustomExtendedDataField {...fieldProps} formId={formId} />
              ))}
            </div>
          ) : null}

          <div className={css.bottomWrapper}>
            {isBuyer ? (
              <div className={css.checkboxesContainer}>
                <FieldCheckboxGroup
                  className={css.marketingCheckbox}
                  name="marketingOptIn"
                  id="marketing-opt-in"
                  optionLabelClassName={css.checkboxLabel}
                  options={[
                    {
                      key: 'marketing',
                      label: 'Yes, send me exclusive offers, promotions, latest news, reviews, and personalized tips for buying and selling on BowTackle.',
                    },
                  ]}
                />
              </div>
            ) : null}
            {termsAndConditions}

            {TURNSTILE_SITE_KEY ? (
              <div className={css.turnstileWrapper}>
                <div ref={turnstileContainerRef} />
              </div>
            ) : null}

            <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
              <FormattedMessage id={isProvider ? 'SignupForm.apply' : 'SignupForm.signUp'} />
            </PrimaryButton>
            <p className={css.logInText}>
              <FormattedMessage id="SignupForm.existingAccountText" values={{ link: link }}/>
            </p>
          </div>
        </Form>
      );
    }}
  />
  );
};

SignupFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  formId: null,
  inProgress: false,
  preselectedUserType: null,
};

SignupFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  formId: string,
  inProgress: bool,
  termsAndConditions: node.isRequired,
  preselectedUserType: string,
  userTypes: propTypes.userTypes.isRequired,
  userFields: propTypes.listingFields.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const SignupForm = compose(injectIntl)(SignupFormComponent);
SignupForm.displayName = 'SignupForm';

export default SignupForm;
